import { useEffect, useMemo, useRef, useState } from 'react';
import { identity, links } from '../brand';
import { OverviewPage } from './OverviewPage';
import { LinksPage } from './LinksPage';
import { adminLinks } from './data';
import { CommandPalette, type Command } from './CommandPalette';
import { StarIcon, SearchIcon, PanelIcon, OverviewIcon, LinkIcon, ChevronIcon, SignOutIcon } from './icons';

type Page = 'overview' | 'links';
const NAV: { id: Page; label: string; icon: () => React.ReactElement }[] = [
  { id: 'overview', label: 'Overview', icon: OverviewIcon },
  { id: 'links', label: 'Links', icon: LinkIcon },
];

const COLLAPSE_KEY = 'djlb_admin_collapsed';

export function AdminShell({ email }: { email: string }) {
  const [page, setPage] = useState<Page>('overview');
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const v = localStorage.getItem(COLLAPSE_KEY);
      if (v != null) return v === '1';
    } catch {
      /* ignore */
    }
    return typeof window !== 'undefined' && window.innerWidth < 768;
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const userRef = useRef<HTMLDivElement>(null);
  const userBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [collapsed]);

  async function logout() {
    setLoggingOut(true);
    await fetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
    window.location.href = '/admin/';
  }

  // ⌘K toggles the palette; Esc closes the account menu (restoring focus).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPaletteOpen((o) => !o);
        return;
      }
      if (e.key === 'Escape' && menuOpen) {
        setMenuOpen(false);
        userBtnRef.current?.focus();
      }
    }
    function onDocClick(e: MouseEvent) {
      if (userRef.current && !userRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('click', onDocClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('click', onDocClick);
    };
  }, [menuOpen]);

  const openUrl = (url: string) => window.open(url, '_blank', 'noopener,noreferrer');

  const commands: Command[] = useMemo(
    () => [
      ...NAV.map((n) => ({ id: `nav-${n.id}`, label: n.label, group: 'Go to', run: () => setPage(n.id) })),
      ...links.map((l) => ({ id: `pub-${l.icon}`, label: l.label, group: 'Public profile', run: () => openUrl(l.url) })),
      ...adminLinks.map((l) => ({ id: `adm-${l.label}`, label: l.label, group: 'Admin & studio', run: () => openUrl(l.url) })),
      { id: 'signout', label: 'Sign out', group: 'Account', run: () => logout() },
    ],
    [],
  );

  return (
    <div className={`dash${collapsed ? ' is-collapsed' : ''}`}>
      {!collapsed && <div className="dash__scrim" onClick={() => setCollapsed(true)} aria-hidden="true" />}

      <aside className="dash__side">
        <div className="dash__top">
          <span className="dash__logo">
            <StarIcon />
          </span>
          <span className="dash__word">Admin console</span>
          <button
            type="button"
            className="dash__collapse"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            onClick={() => setCollapsed((c) => !c)}
          >
            <PanelIcon />
          </button>
        </div>

        <button
          type="button"
          className="dash__search"
          onClick={() => setPaletteOpen(true)}
          aria-label="Search (Command K)"
          title="Search"
        >
          <SearchIcon />
          <span className="dash__search-label">Search</span>
          <kbd className="dash__kbd">⌘K</kbd>
        </button>

        <nav className="dash__nav" aria-label="Primary">
          {NAV.map((n) => (
            <button
              key={n.id}
              type="button"
              className={`dash__navitem${page === n.id ? ' is-active' : ''}`}
              aria-current={page === n.id ? 'page' : undefined}
              aria-label={n.label}
              title={n.label}
              onClick={() => setPage(n.id)}
            >
              <span className="dash__navicon">
                <n.icon />
              </span>
              <span className="dash__label">{n.label}</span>
            </button>
          ))}
        </nav>

        <div className="dash__spacer" />

        <div className="dash__user" ref={userRef}>
          {menuOpen && (
            <div className="dash__usermenu" role="menu">
              <div className="dash__umname">{identity.name}</div>
              <div className="dash__umemail">{email}</div>
              <button
                type="button"
                className="dash__signout"
                role="menuitem"
                onClick={logout}
                disabled={loggingOut}
              >
                <SignOutIcon /> {loggingOut ? 'Signing out…' : 'Sign out'}
              </button>
            </div>
          )}
          <button
            type="button"
            className="dash__userbtn"
            ref={userBtnRef}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="Account"
            title={identity.name}
            onClick={() => setMenuOpen((o) => !o)}
          >
            <img className="dash__avatar" src={identity.avatar} width={28} height={28} alt="" />
            <span className="dash__usermeta">
              <span className="dash__username">{identity.name}</span>
              <span className="dash__useremail">{email}</span>
            </span>
            <span className="dash__userchev">
              <ChevronIcon />
            </span>
          </button>
        </div>
      </aside>

      <main className="dash__content">{page === 'overview' ? <OverviewPage /> : <LinksPage />}</main>

      <CommandPalette open={paletteOpen} commands={commands} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
